import { format, isValid, parse, setHours, setMinutes, setSeconds, startOfDay } from 'date-fns';

export const NO_TIME = '';

export interface ITimeOption {
	value: string;
	label: string;
}

const FIRST_SLOT_HOUR = 6;
const LAST_SLOT_HOUR = 20;
const SLOT_MINUTES = 30;

export function timeKey(date: Date): string {
	return format(date, 'HH:mm');
}

function labelFor(key: string): string {
	return format(parse(key, 'HH:mm', new Date()), 'h:mm a');
}

/** Half-hour slots for the working day, plus the item's own time when it falls between slots. */
export function timeOptionsFor(dueDate: Date | null, hasDueTime: boolean): ITimeOption[] {
	const keys: string[] = [];
	for (let hour = FIRST_SLOT_HOUR; hour <= LAST_SLOT_HOUR; hour++) {
		for (let minute = 0; minute < 60; minute += SLOT_MINUTES) {
			if (hour === LAST_SLOT_HOUR && minute > 0) {
				break;
			}
			keys.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
		}
	}
	if (dueDate && hasDueTime) {
		const current = timeKey(dueDate);
		if (!keys.includes(current)) {
			keys.push(current);
			keys.sort();
		}
	}
	return [{ value: NO_TIME, label: 'No time' }, ...keys.map(key => ({ value: key, label: labelFor(key) }))];
}

/** Combine a calendar day with an `HH:mm` key; `NO_TIME` snaps the day to local midnight. */
export function applyTime(day: Date, key: string): Date {
	const base = startOfDay(day);
	if (key === NO_TIME) {
		return base;
	}
	const [hours, minutes] = key.split(':').map(Number);
	return setSeconds(setMinutes(setHours(base, hours), minutes), 0);
}

/** Parse the `yyyy-MM-dd` string the DateInput emits, keeping the existing time when one is set. */
export function applyDay(value: string, current: Date | null, hasDueTime: boolean): Date | null | undefined {
	if (!value.trim()) {
		return null;
	}
	const parsed = parse(value, 'yyyy-MM-dd', new Date());
	if (!isValid(parsed)) {
		return undefined;
	}
	return current && hasDueTime ? applyTime(parsed, timeKey(current)) : startOfDay(parsed);
}
